/**
 * grid-backfill.js
 *
 * Problem: Shopify assigns products to pages BEFORE our theme hides sold-out
 * size-variant cards (see main-collection-product-grid.liquid). That means a
 * filtered page can render fewer visible cards than the page-size setting,
 * leaving empty-looking gaps, while the products that would fill those gaps
 * sit on the next page instead.
 *
 * This script:
 * 1. Hides any card already shown to this shopper on an earlier page during
 *    this session (tracked in sessionStorage, scoped per filter combination)
 *    - prevents duplicates when a shopper who was backfilled on page 1 then
 *      browses directly to page 2.
 * 2. Backfills the resulting gap by fetching subsequent page(s) via the
 *    Section Rendering API and appending enough additional visible cards to
 *    reach section.settings.products_per_page, recording anything pulled
 *    forward so later pages know to hide it too.
 * 3. Tracks which of those later pages got fully drained in the process
 *    (nothing left to show if a shopper clicked through to them) and removes
 *    their numbered links from the pagination widget - and redirects the
 *    "next" arrow past them - so shoppers never land on a dead-end page.
 * 4. As a safety net, if a page still ends up empty anyway (e.g. a shopper
 *    lands on it directly in a fresh session before any draining info
 *    exists), redirects back to page 1 rather than showing a blank grid.
 *
 * Fires a `grid:backfilled` event on the grid element when done, with
 * `detail.changed` set to true only if this script actually removed or
 * added any cards. facet-preview-counts.js does its own independent
 * DOM-mutation-triggered recompute rather than listening for this event
 * directly, so `changed` isn't currently consumed anywhere - kept for any
 * future listener, and because it costs nothing to report accurately.
 *
 * Feature added 2026-09-15: added search results support
 * (sections/main-search.liquid). That template's #product-grid is a
 * DIFFERENT kind of element than the collection template's: on
 * main-collection-product-grid.liquid, #product-grid IS the <ul> whose
 * children are the product <li> cards directly; on main-search.liquid,
 * #product-grid is a wrapping <div> (sibling to a .loading-overlay div and
 * containing a <ul class="product-grid"> one level down) - its own children
 * are NOT the <li> cards. Every place below that used to read/append to
 * "the grid's children" now resolves the actual item list via
 * getItemsList() first, so this works correctly against either shape
 * without needing to know which template rendered the page. Nothing else
 * needed a template-specific change: getCurrentPage/buildPageUrl/
 * getFilterKey all operate generically on the current URL (search's `q`
 * param included) and toSectionUrl's Section Rendering API fetch works
 * the same for any section, not just collection ones.
 */
(function () {
  function getCurrentPage() {
    var params = new URLSearchParams(window.location.search);
    var page = parseInt(params.get('page'), 10);
    return page && page > 0 ? page : 1;
  }

  function getMaxPage(wrapper) {
    var nums = Array.from(wrapper.querySelectorAll('.pagination__list .pagination__item'))
      .map(function (el) {
        return parseInt(el.textContent.trim(), 10);
      })
      .filter(function (n) {
        return !isNaN(n);
      });
    return nums.length ? Math.max.apply(null, nums) : getCurrentPage();
  }

  function buildPageUrl(pageNumber) {
    var url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber);
    return url.toString();
  }

  function toSectionUrl(pageUrl, sectionId) {
    var url = new URL(pageUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  // Feature added 2026-09-15 (2): on /search, a Section Rendering API
  // fetch of "page N" and a real full navigation to that same page N can
  // return GENUINELY DIFFERENT results - confirmed live on
  // /search?q=red: fetching page 3 via section_id returned only 9
  // products, while actually navigating to page 3 showed 24 real, distinct
  // ones (the raw results_count itself even varied between requests - 707
  // vs 701 for the same query). Shopify's full-text search on a broad,
  // loosely-relevant query like this isn't perfectly deterministic
  // per-request the way collection.products (a stable, deterministically
  // sorted list) is. The backfill/gap-filling walk below still works fine
  // under that uncertainty - it just pulls whatever real products a
  // forward fetch happens to return, which is still real, valid content,
  // possibly just needing a couple more fetches than expected. But
  // treating an under-reported fetch as proof a page is "drained" is NOT
  // safe: that's what caused real, populated pages to have their
  // pagination links removed and get skipped by the "next" arrow. So
  // drained-page tracking still happens (harmless to compute), but
  // updatePaginationUI() - the part that actually hides links / redirects
  // the arrow based on it - is skipped on search specifically.
  function isSearchPage() {
    return /(^|\/)search\/?$/.test(window.location.pathname);
  }

  // Feature added 2026-09-15: the actual product-card list isn't always
  // the same element #product-grid resolves to - see the file header note
  // above. If `grid` itself is the <ul class="product-grid"> (collection
  // template), it IS the item list. Otherwise (search template), the item
  // list is a descendant <ul class="product-grid"> one level down. Falls
  // back to `grid` itself if neither shape matches, rather than throwing,
  // so an unexpected future markup change degrades to the old behavior
  // instead of breaking outright.
  function getItemsList(grid) {
    if (grid.matches && grid.matches('ul.product-grid')) return grid;
    return grid.querySelector('ul.product-grid') || grid;
  }

  function getFilterKey() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    var keys = Array.from(params.keys()).sort();
    var normalized = keys.map(function (k) { return k + '=' + params.getAll(k).sort().join(','); }).join('&');
    return 'backfillConsumed:' + window.location.pathname + '?' + normalized;
  }

  function getConsumed() {
    try {
      return JSON.parse(sessionStorage.getItem(getFilterKey()) || '[]');
    } catch (e) {
      return [];
    }
  }

  function addConsumed(handles) {
    if (!handles.length) return;
    var existing = getConsumed();
    var merged = Array.from(new Set(existing.concat(handles)));
    try {
      sessionStorage.setItem(getFilterKey(), JSON.stringify(merged));
    } catch (e) {
      /* sessionStorage unavailable - dedupe across pages just won't persist */
    }
  }

  // Consumed-tracking only exists to stop a product appearing twice within
  // ONE continuous visit: backfilled onto page 1, then also encountered by
  // navigating to its real page later in that same visit. It is NOT meant
  // to survive across separate visits to page 1 of the same query - but
  // sessionStorage never expires on its own, so without this reset, any
  // handle ever recorded (including from earlier testing under a different
  // set of filters/cards, or a previous, unrelated visit) permanently
  // suppresses that product every time this exact query's page 1 loads
  // again in the same tab session. Page 1 loading is the moment backfill
  // decides fresh what should show, so it should never carry forward
  // memory from a prior page-1 visit.
  function clearConsumed() {
    try {
      sessionStorage.removeItem(getFilterKey());
    } catch (e) {
      /* sessionStorage unavailable - nothing to clear */
    }
  }

  // Returns the number of cards removed, so callers can tell whether
  // anything actually changed. Operates on the resolved item list, not
  // necessarily on the element #product-grid itself - see getItemsList().
  function hideAlreadyConsumed(itemsList) {
    var consumed = getConsumed();
    if (!consumed.length) return 0;
    var removed = 0;
    Array.from(itemsList.children).forEach(function (li) {
      var handle = li.dataset.productHandle;
      if (handle && consumed.indexOf(handle) !== -1) {
        li.remove();
        removed += 1;
      }
    });
    return removed;
  }

  // Removes numbered pagination links for pages we've just determined are
  // fully drained, and redirects the "next" arrow (Dawn quirk: the visually
  // "next" arrow carries the class pagination__item--prev) past them.
  function updatePaginationUI(wrapper, drainedPages, maxPage) {
    if (!drainedPages.length) return;
    var list = wrapper.querySelector('.pagination__list');
    if (!list) return;

    drainedPages.forEach(function (pageNum) {
      var items = Array.from(list.querySelectorAll('.pagination__item'));
      var match = items.find(function (el) {
        return !el.classList.contains('pagination__item-arrow') && el.textContent.trim() === String(pageNum);
      });
      if (match) {
        var li = match.closest('li');
        if (li) li.remove();
      }
    });

    // Feature added 2026-10-12: once drained pages are removed the
    // remaining numbers can have gaps (e.g. "1  3  >"), which looks like a
    // bug. Renumber the visible links 1, 2, 3... in order. The hrefs are
    // untouched, so each link still goes to its real page; only the label
    // changes. The current page keeps its own position in the sequence.
    var visibleNums = Array.from(list.querySelectorAll('.pagination__item'))
      .filter(function (el) { return !el.classList.contains('pagination__item-arrow'); });
    visibleNums.forEach(function (el, i) {
      var label = String(i + 1);
      if (el.textContent.trim() !== label) el.textContent = label;
      if (el.hasAttribute('aria-label')) el.setAttribute('aria-label', 'Page ' + label);
    });

    var nextLink = list.querySelector('.pagination__item-arrow.pagination__item--prev');
    if (nextLink) {
      var url = new URL(nextLink.getAttribute('href'), window.location.origin);
      var targetPage = parseInt(url.searchParams.get('page'), 10) || 2;
      while (drainedPages.indexOf(targetPage) !== -1 && targetPage <= maxPage) {
        targetPage += 1;
      }
      if (targetPage > maxPage) {
        var nextLi = nextLink.closest('li');
        if (nextLi) nextLi.remove();
      } else {
        url.searchParams.set('page', targetPage);
        nextLink.setAttribute('href', url.toString());
      }
    }
  }

  async function backfill(grid) {
    var targetCount = parseInt(grid.dataset.productsPerPage, 10) || 0;
    var sectionId = grid.dataset.id;
    var currentPage = getCurrentPage();
    var itemsList = getItemsList(grid);

    // Landing on page 1 fresh: wipe any consumed-tracking left over from an
    // earlier, separate visit to this exact query before deciding what to
    // hide. See clearConsumed() for why this matters.
    if (currentPage === 1) {
      clearConsumed();
    }

    // Step 1: remove anything this shopper already saw on another page
    // DURING THIS VISIT (only possible now for page 2+, since page 1 just
    // reset the memory above).
    var removedCount = hideAlreadyConsumed(itemsList);
    var addedCount = 0;

    if (!targetCount || !sectionId) {
      grid.dispatchEvent(new CustomEvent('grid:backfilled', { bubbles: true, detail: { changed: removedCount > 0 } }));
      return;
    }

    var wrapper = document.getElementById('ProductGridContainer') || document;
    var maxPage = getMaxPage(wrapper);
    var nextPage = currentPage + 1;
    var safetyLimit = 20; // hard stop so a bug here can never fetch unbounded pages
    var fetches = 0;
    var drainedPages = [];

    // Feature added 2026-09-15 (3): previously fetched one page at a
    // time, fully sequentially - await page N+1's full round trip
    // (measured ~750KB for one real section response), process it, THEN
    // start fetching page N+2, and so on. Whenever a gap needed more than
    // one extra page to fill (common on search, where a single forward
    // page can under-deliver - see the isSearchPage() comment above), that
    // showed up as several distinct visible "batches" of new items
    // popping in one after another, each with its own loading pause.
    // Fetching a small batch of pages CONCURRENTLY, and only touching the
    // DOM once per batch instead of once per page, cuts both the total
    // wall-clock time (round trips overlap instead of queueing) and the
    // number of distinct visible pauses. BATCH_SIZE is a modest 3 rather
    // than "fetch everything up to maxPage at once" - the common case only
    // needs one extra page's worth of items, so a small batch keeps the
    // typical case to a single round trip without routinely over-fetching
    // pages nobody ends up needing.
    //
    // Bumped 3 -> 8 (2026-09-15, same day) after live testing on
    // /search?q=red showed a real gap needing 2 full rounds (6 pages) to
    // fill - each fetched search page was only contributing ~9 unique
    // items rather than a full 24, for the same per-request search
    // instability documented above. 8 comfortably covers that observed
    // case in a single round. The extra parallel requests when a gap
    // turns out to need fewer pages than the batch size are cheap (they
    // just get discarded via the stillNeeded <= 0 check below) - the
    // expensive thing to avoid is sequential ROUNDS, not request count.
    var BATCH_SIZE = 8;

    while (itemsList.children.length < targetCount && nextPage <= maxPage && fetches < safetyLimit) {
      var batchPages = [];
      while (batchPages.length < BATCH_SIZE && nextPage <= maxPage && fetches < safetyLimit) {
        batchPages.push(nextPage);
        nextPage += 1;
        fetches += 1;
      }

      var batchResults = await Promise.all(
        batchPages.map(function (pageNum) {
          var sectionUrl = toSectionUrl(buildPageUrl(pageNum), sectionId);
          return fetch(sectionUrl)
            .then(function (response) { return { pageNum: pageNum, response: response }; })
            .catch(function () { return { pageNum: pageNum, response: null }; });
        })
      );
      // Promise.all preserves input order already, but sort explicitly -
      // page order matters below (earlier pages' items are preferred to
      // fill the gap first), and relying on unspecified resolution timing
      // would be fragile.
      batchResults.sort(function (a, b) { return a.pageNum - b.pageNum; });

      var newItemsThisBatch = [];
      var stopAfterThisBatch = false;

      for (var i = 0; i < batchResults.length; i++) {
        var result = batchResults[i];
        var response = result.response;
        if (!response || !response.ok) {
          stopAfterThisBatch = true;
          break;
        }

        var html = await response.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fetchedGrid = doc.getElementById('product-grid');
        if (!fetchedGrid) {
          stopAfterThisBatch = true;
          break;
        }

        // Bug fixed 2026-09-15: a FETCHED page that genuinely has zero
        // results (e.g. past the real end of results) renders Liquid's
        // "no products found" / zero-results-recovery markup instead of a
        // <ul class="product-grid"> at all - #product-grid IS that message
        // block directly in that case, with no nested list. getItemsList()'s
        // fallback-to-self is only safe for OUR OWN live grid (guaranteed
        // by scan()'s selector to actually contain a real list), not for
        // an arbitrary fetched page. Deliberately NOT using getItemsList()
        // here - no <ul class="product-grid"> found means no real items on
        // this page, full stop; falling back to the wrapper's own children
        // (the "No products found" heading, the "Shop by category"
        // recovery pills) would scrape THAT content in as if it were
        // product cards and append it into the real grid - confirmed live:
        // exactly this, repeated once per drained page fetched ahead,
        // produced multiple stacked "No products found / Shop by
        // category" blocks on a real /search?q=red page.
        // Bug fixed 2026-10-12: on the COLLECTION template #product-grid IS
        // the <ul class="product-grid"> itself, so querySelector() (which
        // only searches descendants) always returned null there - every
        // fetched page was treated as empty/"drained", nothing was ever
        // appended, and the pagination links were then removed. Only the
        // search template nests the <ul> inside a wrapper <div>.
        var fetchedItemsList = fetchedGrid.matches('ul.product-grid')
          ? fetchedGrid
          : fetchedGrid.querySelector('ul.product-grid');

        // Skip anything already consumed on a prior page OR earlier in
        // THIS batch (needed now that a batch can pull from several pages
        // in one pass - consumedSoFar has to include this batch's own
        // in-progress picks, not just what was already persisted before
        // the batch started).
        var consumedSoFar = getConsumed().concat(
          newItemsThisBatch.map(function (item) { return item.dataset.productHandle; }).filter(Boolean)
        );
        var candidates = fetchedItemsList
          ? Array.from(fetchedItemsList.children).filter(function (li) {
              var handle = li.dataset.productHandle;
              return !handle || consumedSoFar.indexOf(handle) === -1;
            })
          : [];

        if (candidates.length === 0) {
          // Nothing left on this page at all - fully drained.
          drainedPages.push(result.pageNum);
          continue;
        }

        var stillNeeded = targetCount - itemsList.children.length - newItemsThisBatch.length;
        if (stillNeeded <= 0) continue; // already satisfied earlier in this same batch
        var newItems = candidates.slice(0, stillNeeded);
        newItemsThisBatch = newItemsThisBatch.concat(newItems);

        if (candidates.length <= stillNeeded) {
          // We took everything this page had - nothing left for a direct visit.
          drainedPages.push(result.pageNum);
        }
      }

      // Single DOM update for the whole batch, instead of one per page -
      // this is the other half of what collapses several visible "spins"
      // into one.
      newItemsThisBatch.forEach(function (item) {
        itemsList.appendChild(item);
      });
      addedCount += newItemsThisBatch.length;
      addConsumed(
        newItemsThisBatch.map(function (item) { return item.dataset.productHandle; }).filter(Boolean)
      );

      if (stopAfterThisBatch) break;
    }

    // See isSearchPage()'s comment above - a page a search forward-fetch
    // reports as empty isn't reliably actually empty, so don't act on
    // that by hiding its link or redirecting the arrow past it here.
    // Collections are unaffected (deterministic product listing, so this
    // signal is trustworthy there) - full original behavior retained.
    if (!isSearchPage()) {
      updatePaginationUI(wrapper, drainedPages, maxPage);
    }

    // Safety net: if this page still ends up with nothing to show (e.g. a
    // shopper lands directly on it in a fresh session, before any draining
    // info exists elsewhere), bounce back to page 1 rather than show a
    // blank grid.
    if (itemsList.children.length === 0 && currentPage > 1 && maxPage > 1) {
      var page1Url = new URL(window.location.href);
      page1Url.searchParams.delete('page');
      window.location.replace(page1Url.toString());
      return;
    }

    var changed = removedCount > 0 || addedCount > 0;
    grid.dispatchEvent(new CustomEvent('grid:backfilled', { bubbles: true, detail: { changed: changed } }));
  }

  function scan() {
    document.querySelectorAll('#product-grid[data-products-per-page]').forEach(function (grid) {
      if (grid.dataset.backfillInit) return;
      grid.dataset.backfillInit = 'true';
      backfill(grid);
    });
  }

  // Feature added 2026-09-15: closes a gap where a shopper landing
  // DIRECTLY on a page that's genuinely empty (Liquid's "no results"
  // branch - which never gets data-products-per-page, since there's
  // nothing there for the normal backfill flow to act on) had no path
  // back to real content. scan() never finds a grid to initialize on
  // that branch at all, so even the existing safety-net redirect inside
  // backfill() (which only runs once backfill() is already running on
  // SOME grid) never gets a chance to fire. Confirmed live: /search?q=red
  // reported 30 pages via its pagination widget, but pages 26-30 were
  // genuinely empty and stayed blank indefinitely with no recovery -
  // reachable both by clicking a numbered link directly, and by the
  // "next" arrow stepping there before updatePaginationUI() had a chance
  // to catch and remove that specific link (it only ever processes pages
  // encountered during backfill()'s own forward-fetch walk from wherever
  // a shopper currently is, not a full upfront audit of the entire
  // pagination range - deliberately so, since exhaustively fetching every
  // page just to check for emptiness would be wasteful, especially
  // against something like 30 pages).
  //
  // This runs independently of the backfill()/fetch-forward flow - it
  // doesn't try to pull products in from later pages to fill a page
  // Liquid has already told us has zero matches (for a well-behaved
  // paginated result set, once a page is genuinely empty, later pages
  // don't meaningfully "resume" having content), it just bounces back to
  // page 1 immediately, the same fallback the in-flight safety net
  // already uses elsewhere in this file. Requires the theme's Liquid
  // template to render `data-backfill-empty="true"` on the "no results"
  // branch's #product-grid element - see main-search.liquid /
  // main-collection-product-grid.liquid.
  //
  // Returns true if a redirect was triggered (caller should treat that as
  // "stop, navigation is already in progress" rather than continuing on
  // to scan()).
  function redirectIfLandedOnEmptyPage() {
    var emptyGrid = document.querySelector('#product-grid[data-backfill-empty]');
    if (!emptyGrid) return false;
    if (getCurrentPage() <= 1) return false;
    var page1Url = new URL(window.location.href);
    page1Url.searchParams.delete('page');
    window.location.replace(page1Url.toString());
    return true;
  }

  function init() {
    if (redirectIfLandedOnEmptyPage()) return;
    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Dawn swaps in a fresh grid element (new DOM node) whenever a filter
  // changes via AJAX, so watch for that and re-scan. Also re-checks the
  // empty-landing redirect above, since an AJAX filter change can just as
  // easily leave a shopper looking at a genuinely empty page (e.g.
  // narrowing filters while already on page 3) as a full page load can.
  var wrapper = document.getElementById('ProductGridContainer');
  if (wrapper) {
    new MutationObserver(init).observe(wrapper, { childList: true, subtree: true });
  }
})();